
import React from 'react';
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles, Send, Lightbulb, Play, Download } from "lucide-react";
import { Project } from '@/hooks/useProjects';
import { useProjectRrwebSessions } from '@/hooks/useProjectRrwebSessions';
import { useProjectSlides } from '@/hooks/useProjectSlides';
import { useProjectAudioElements } from '@/hooks/useProjectAudio';

interface AIPanelProps {
  project: Project;
}

const AIPanel = ({ project }: AIPanelProps) => {
  const { data: rrwebSessions } = useProjectRrwebSessions(project.id);
  const { data: slides } = useProjectSlides(project.id);
  const { data: audioElements } = useProjectAudioElements(project.id);
  const [message, setMessage] = React.useState('');

  const hasContent = (rrwebSessions?.length || 0) > 0 || (slides?.length || 0) > 0;
  const hasAudio = (audioElements?.length || 0) > 0;

  const getSuggestions = () => {
    const suggestions = [];
    
    if (!hasContent) {
      suggestions.push({
        icon: Play,
        text: "Record your screen or add slides to get started",
        action: "help-getting-started"
      });
    }
    
    if (hasContent && !hasAudio) {
      suggestions.push({
        icon: Sparkles,
        text: "Generate voiceover narration",
        action: "generate-narration"
      });
    }
    
    if (hasContent) {
      suggestions.push({
        icon: Lightbulb,
        text: "Add background music",
        action: "add-music"
      });
    }
    
    if (hasContent && project.export_status !== 'in_progress') {
      suggestions.push({
        icon: Download,
        text: "Export your video",
        action: "export-video"
      });
    }

    return suggestions;
  };

  const handleSendMessage = () => {
    if (!message.trim()) return;
    
    console.log('AI Message sent:', message);
    // TODO: Implement AI chat functionality
    setMessage('');
  };

  const handleSuggestionClick = (action: string) => {
    console.log('AI Suggestion clicked:', action);
    // TODO: Implement suggestion actions
  };

  return (
    <div className="w-80 bg-editor-medium border-l border-editor-light p-4 flex flex-col">
      <div className="flex items-center mb-4">
        <Sparkles className="h-5 w-5 text-editor-accent mr-2" />
        <h3 className="font-semibold">AI Assistant</h3>
      </div>
      
      <div className="flex-1 space-y-4 overflow-y-auto">
        {/* Welcome Message */}
        <div className="bg-editor-dark p-3 rounded-lg">
          <p className="text-sm text-gray-300">
            I'm here to help you create and edit your video project "{project.title}". 
            {hasContent ? "Your project is looking great!" : "Let's get started by adding some content."}
          </p>
        </div>
        
        {/* Project Status */}
        <div className="bg-editor-dark p-3 rounded-lg">
          <h4 className="text-sm font-medium text-gray-200 mb-2">Project Status</h4>
          <div className="space-y-1 text-xs text-gray-400">
            <div>📹 Screen recordings: {rrwebSessions?.length || 0}</div>
            <div>📊 Slides: {slides?.length || 0}</div>
            <div>🎵 Audio elements: {audioElements?.length || 0}</div>
            <div>📺 Export status: {project.export_status || 'none'}</div>
          </div>
        </div>

        {/* AI Suggestions */}
        <div className="space-y-2">
          <div className="flex items-center">
            <Lightbulb className="h-4 w-4 text-editor-accent mr-2" />
            <p className="text-xs text-gray-500 uppercase tracking-wide">Smart Suggestions</p>
          </div>
          <div className="space-y-1">
            {getSuggestions().map((suggestion, index) => (
              <Button
                key={index}
                variant="ghost"
                size="sm"
                className="w-full justify-start text-left h-auto py-2 px-3 hover:bg-editor-dark"
                onClick={() => handleSuggestionClick(suggestion.action)}
              >
                <suggestion.icon className="h-4 w-4 mr-2 text-editor-accent flex-shrink-0" />
                <span className="text-sm">{suggestion.text}</span>
              </Button>
            ))}
          </div>
        </div>

        {/* Recent Activity */}
        {project.last_edited_at && (
          <div className="bg-editor-dark p-3 rounded-lg">
            <h4 className="text-sm font-medium text-gray-200 mb-2">Recent Activity</h4>
            <p className="text-xs text-gray-400">
              Last edited: {new Date(project.last_edited_at).toLocaleDateString()} at{' '}
              {new Date(project.last_edited_at).toLocaleTimeString([], { 
                hour: '2-digit', 
                minute: '2-digit' 
              })}
            </p>
          </div>
        )}
      </div>
      
      {/* Chat Input */}
      <div className="space-y-2 mt-4 border-t border-editor-light pt-4">
        <Textarea
          placeholder="Ask me anything about your video project..."
          className="bg-editor-dark border-editor-light text-white placeholder:text-gray-400 resize-none"
          rows={3}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSendMessage();
            }
          }}
        />
        <Button 
          size="sm" 
          className="w-full bg-editor-accent hover:bg-editor-highlight"
          onClick={handleSendMessage}
          disabled={!message.trim()}
        >
          <Send className="h-4 w-4 mr-2" />
          Send Message
        </Button>
      </div>
    </div>
  );
};

export default AIPanel;
